/** Print template component catalog & defaults (order receipts). */

export const PAPER_SIZES = {
  A4: { key: "A4", label: "A4", widthMm: 210, cols: 12 },
  MM80: { key: "80mm", label: "80mm", widthMm: 80, cols: 8 },
  MM58: { key: "58mm", label: "58mm", widthMm: 58, cols: 8 },
};

export const COMPONENT_GROUPS = [
  {
    key: "text",
    labelKey: "order.printTemplate.groups.text",
    items: [
      { type: "custom-text", labelKey: "order.printTemplate.comps.customText", icon: "font-size" },
      { type: "field", field: "receiver", labelKey: "order.printTemplate.comps.customerName", icon: "user" },
      { type: "field", field: "notes", labelKey: "order.printTemplate.comps.orderNotes", icon: "file-text" },
      { type: "field", field: "merchant_name", labelKey: "order.printTemplate.comps.merchantName", icon: "shop" },
      { type: "field", field: "merchant_tel", labelKey: "order.printTemplate.comps.merchantTel", icon: "phone" },
      { type: "field", field: "created_at", labelKey: "order.printTemplate.comps.orderTime", icon: "clock-circle" },
      { type: "field", field: "print_time", labelKey: "order.printTemplate.comps.printTime", icon: "clock-circle" },
      { type: "field", field: "order_no", labelKey: "order.printTemplate.comps.orderNo", icon: "number" },
      { type: "field", field: "address", labelKey: "order.printTemplate.comps.address", icon: "environment" },
      { type: "field", field: "tel", labelKey: "order.printTemplate.comps.customerTel", icon: "mobile" },
      { type: "field", field: "paytype_label", labelKey: "order.printTemplate.comps.payType", icon: "wallet" },
      { type: "field", field: "ship_price", labelKey: "order.printTemplate.comps.shipPrice", icon: "car" },
      { type: "field", field: "total", labelKey: "order.printTemplate.comps.totalAmount", icon: "money-collect" },
    ],
  },
  {
    key: "qrcode",
    labelKey: "order.printTemplate.groups.qrcode",
    items: [
      { type: "qrcode", field: "order_url", labelKey: "order.printTemplate.comps.orderQr", icon: "qrcode" },
      { type: "qrcode", field: "custom", labelKey: "order.printTemplate.comps.customQr", icon: "qrcode" },
    ],
  },
  {
    key: "image",
    labelKey: "order.printTemplate.groups.image",
    items: [
      { type: "image", field: "logo", labelKey: "order.printTemplate.comps.logo", icon: "picture" },
      { type: "image", field: "custom", labelKey: "order.printTemplate.comps.customImage", icon: "file-image" },
    ],
  },
  {
    key: "barcode",
    labelKey: "order.printTemplate.groups.barcode",
    items: [
      { type: "barcode", field: "order_no", labelKey: "order.printTemplate.comps.orderBarcode", icon: "barcode" },
      { type: "barcode", field: "custom", labelKey: "order.printTemplate.comps.customBarcode", icon: "barcode" },
    ],
  },
  {
    key: "products",
    labelKey: "order.printTemplate.groups.products",
    items: [
      { type: "product-table", variant: "line", labelKey: "order.printTemplate.comps.productLines", icon: "table" },
      { type: "product-table", variant: "summary", labelKey: "order.printTemplate.comps.productSummary", icon: "unordered-list" },
    ],
  },
  {
    key: "other",
    labelKey: "order.printTemplate.groups.other",
    items: [
      { type: "divider", labelKey: "order.printTemplate.comps.divider", icon: "minus" },
      { type: "spacer", labelKey: "order.printTemplate.comps.spacer", icon: "column-height" },
    ],
  },
];

/** Available product-table columns (mapped to our order detail fields). */
export const LOCKED_INDEX_COLUMN = {
  key: "index",
  label: "序号",
  width: 8,
  locked: true,
};

export const PRODUCT_COLUMNS = [
  { key: "index", labelKey: "order.printTemplate.cols.index", defaultLabel: "序号", defaultWidth: 8, locked: true },
  { key: "image", labelKey: "order.printTemplate.cols.image", defaultLabel: "图片", defaultWidth: 12 },
  { key: "name", labelKey: "order.printTemplate.cols.name", defaultLabel: "商品名称", defaultWidth: 36 },
  { key: "sku", labelKey: "order.printTemplate.cols.sku", defaultLabel: "规格", defaultWidth: 16 },
  { key: "name_sku", labelKey: "order.printTemplate.cols.nameSku", defaultLabel: "商品/规格", defaultWidth: 40 },
  { key: "barcode", labelKey: "order.printTemplate.cols.barcode", defaultLabel: "条码", defaultWidth: 18 },
  { key: "unit", labelKey: "order.printTemplate.cols.unit", defaultLabel: "单位", defaultWidth: 10 },
  { key: "amount", labelKey: "order.printTemplate.cols.amount", defaultLabel: "数量", defaultWidth: 12 },
  { key: "price", labelKey: "order.printTemplate.cols.price", defaultLabel: "单价", defaultWidth: 14 },
  { key: "line_total", labelKey: "order.printTemplate.cols.lineTotal", defaultLabel: "金额", defaultWidth: 14 },
  { key: "remark", labelKey: "order.printTemplate.cols.remark", defaultLabel: "备注", defaultWidth: 16 },
];

/** Always keep 序号 as the first, non-deletable column. */
export function ensureIndexColumn(columns = []) {
  const existing = (columns || []).find((c) => c.key === "index");
  const rest = (columns || []).filter((c) => c.key !== "index");
  return [
    {
      ...LOCKED_INDEX_COLUMN,
      ...(existing?.width != null ? { width: existing.width } : {}),
      ...(existing?.label ? { label: existing.label } : {}),
    },
    ...rest,
  ];
}

export const uid = (prefix = "c") =>
  `${prefix}-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 7)}`;

export function createComponent(paletteItem) {
  const id = uid("comp");
  const base = {
    id,
    type: paletteItem.type,
    startCol: 1,
    span: 12,
    marginTop: 4,
    fontSize: 12,
    bold: false,
    align: "left",
  };

  switch (paletteItem.type) {
    case "custom-text":
      return {
        ...base,
        text: "",
        label: paletteItem.labelKey,
      };
    case "field":
      return {
        ...base,
        field: paletteItem.field,
        showLabel: true,
        labelText: "",
        label: paletteItem.labelKey,
      };
    case "image":
      return {
        ...base,
        field: paletteItem.field || "logo",
        url: "",
        height: 48,
        span: 4,
        besideRows: 2,
        align: "center",
        label: paletteItem.labelKey,
      };
    case "barcode":
      return {
        ...base,
        field: paletteItem.field || "order_no",
        value: "",
        // WeChat scans QR reliably; long order_no Code128 is usually unreadable on phone
        barcodeFormat: paletteItem.field === "custom" ? "code128" : "qr",
        height: paletteItem.field === "custom" ? 48 : 120,
        size: 120,
        span: paletteItem.field === "custom" ? 12 : 3,
        besideRows: paletteItem.field === "custom" ? 0 : 3,
        showValue: true,
        align: "center",
        label: paletteItem.labelKey,
      };
    case "qrcode":
      return {
        ...base,
        field: paletteItem.field || "order_url",
        value: "",
        size: 80,
        align: "center",
        span: 4,
        besideRows: 3,
        label: paletteItem.labelKey,
      };
    case "divider":
      return {
        ...base,
        style: "solid",
        thickness: 1,
        label: paletteItem.labelKey,
      };
    case "spacer":
      return {
        ...base,
        height: 12,
        label: paletteItem.labelKey,
      };
    case "product-table":
      return {
        ...base,
        variant: paletteItem.variant || "line",
        showHeader: true,
        border: "none",
        sort: "data",
        showSummary: true,
        summaryLabel: "合计",
        summaryColumns: ["amount", "line_total"],
        headerFontSize: 9,
        headerBold: true,
        bodyFontSize: 9,
        bodyBold: false,
        columns: ensureIndexColumn([
          { key: "name_sku", label: "商品/规格", width: 32 },
          { key: "barcode", label: "条码", width: 16 },
          { key: "amount", label: "数量", width: 12 },
          { key: "price", label: "单价", width: 16 },
          { key: "line_total", label: "金额", width: 16 },
        ]),
        label: paletteItem.labelKey,
      };
    default:
      return { ...base, label: paletteItem.labelKey };
  }
}

export function createRow(children = []) {
  return {
    id: uid("row"),
    type: "row",
    children,
  };
}

/** Sum of column spans used in a row (max useful = 12). */
export function rowUsedSpan(row) {
  return (row?.children || []).reduce(
    (sum, c) => sum + Math.min(12, Math.max(1, c.span || 12)),
    0
  );
}

export function isTallMedia(comp) {
  if (!comp) return false;
  if (comp.type === "image" || comp.type === "qrcode") return true;
  if (comp.type === "barcode") {
    const fmt =
      comp.barcodeFormat || (comp.field === "order_no" ? "qr" : "code128");
    return fmt === "qr";
  }
  return false;
}

/** How many content rows may sit beside a tall QR/image (0 = none). */
export function getBesideRows(tall) {
  if (!tall) return 0;
  if (tall.besideRows == null || tall.besideRows === "") return 3;
  const n = Number(tall.besideRows);
  if (Number.isNaN(n)) return 3;
  return Math.max(0, Math.min(20, Math.floor(n)));
}

/** Map a page-level span into the side-stack's internal 12-col grid. */
export function toStackInnerSpan(pageSpan, sideSpan = 10) {
  const s = Math.min(12, Math.max(1, pageSpan || 12));
  const side = Math.min(12, Math.max(1, sideSpan || 10));
  if (s >= side || s === 12) return 12;
  return s;
}

/**
 * Pack into rows only (no QR side-fold). Used inside the beside-QR stack
 * so e.g. 6+6 name/title share one side row.
 */
export function packRowsOnly(components = []) {
  const rows = [];
  let bucket = [];
  let used = 0;
  for (const comp of components) {
    if (!comp || comp.type === "stack") continue;
    const span = Math.min(12, Math.max(1, comp.span || 12));
    if (bucket.length && used + span > 12) {
      rows.push(createRow(bucket));
      bucket = [];
      used = 0;
    }
    bucket.push(comp);
    used += span;
  }
  if (bucket.length) rows.push(createRow(bucket));
  return rows;
}

export function createStack(children = [], span = 10) {
  const sideSpan = Math.min(12, Math.max(1, span));
  return {
    id: uid("stack"),
    type: "stack",
    span: sideSpan,
    children: (children || []).map((c) => ({
      ...c,
      span: toStackInnerSpan(c.span, sideSpan),
    })),
    label: "order.printTemplate.sideStack",
  };
}

/** Flatten stack containers to leaf components (for outline counts / some ops). */
export function flattenRowChildren(children = []) {
  const out = [];
  (children || []).forEach((c) => {
    if (c?.type === "stack") {
      out.push(...(c.children || []));
    } else if (c) {
      out.push(c);
    }
  });
  return out;
}

/**
 * Pack components into 12-column rows left-to-right,
 * then optionally fold following rows into a vertical stack beside tall media.
 * @param {object[]} components
 * @param {{ fold?: boolean }} [options] fold=false keeps existing breakBeside rows stable
 */
export function packComponentsIntoRows(components = [], options = {}) {
  const fold = options.fold !== false;
  const rows = [];
  let bucket = [];
  let used = 0;
  for (const comp of components) {
    if (comp?.type === "stack") {
      if (bucket.length) {
        rows.push(createRow(bucket));
        bucket = [];
        used = 0;
      }
      rows.push(createRow([comp]));
      continue;
    }
    // Keep QR/logo alone so following fields can fold into the side stack
    if (isTallMedia(comp) && (comp.span || 12) < 12) {
      if (bucket.length) {
        rows.push(createRow(bucket));
        bucket = [];
        used = 0;
      }
      rows.push(createRow([comp]));
      continue;
    }
    // Detached from beside-QR group: always its own packing unit so it won't
    // share a row in a way that confuses later fold passes.
    if (comp?.breakBeside) {
      if (bucket.length) {
        rows.push(createRow(bucket));
        bucket = [];
        used = 0;
      }
      rows.push(createRow([{ ...comp, span: Math.min(12, Math.max(1, comp.span || 12)) }]));
      continue;
    }
    const span = Math.min(12, Math.max(1, comp.span || 12));
    if (bucket.length && used + span > 12) {
      rows.push(createRow(bucket));
      bucket = [];
      used = 0;
    }
    bucket.push(comp);
    used += span;
  }
  if (bucket.length) rows.push(createRow(bucket));
  return fold ? foldBesideTallMedia(rows) : rows;
}

/**
 * If a row has a tall QR/image taking e.g. 2 cols, fold later rows into a
 * vertical side stack. Each folded page-row counts as one beside-row; items
 * inside keep their spans so 6+6 can sit on one side row.
 */
export function foldBesideTallMedia(rows = []) {
  const result = [];
  let i = 0;
  while (i < rows.length) {
    const row = rows[i];
    const kids = [...(row.children || [])];
    const tallIdx = kids.findIndex(
      (c) => c.type !== "stack" && isTallMedia(c) && (c.span || 12) < 12
    );
    if (tallIdx < 0) {
      result.push(row);
      i += 1;
      continue;
    }

    const tall = kids[tallIdx];
    const sideSpan = 12 - Math.min(12, Math.max(1, tall.span || 12));
    const maxBeside = getBesideRows(tall);
    let sideItems = [];
    kids.forEach((c, idx) => {
      if (idx === tallIdx) return;
      if (c.type === "stack") {
        sideItems.push(...(c.children || []));
      } else {
        sideItems.push({
          ...c,
          span: toStackInnerSpan(c.span, sideSpan),
        });
      }
    });

    let j = i + 1;
    while (j < rows.length && sideSpan > 0) {
      const nextKids = rows[j].children || [];
      if (!nextKids.length) {
        j += 1;
        continue;
      }
      // Explicitly detached items stop the beside fold (user dragged them out)
      if (nextKids.some((c) => c.breakBeside)) {
        break;
      }
      if (
        nextKids.some(
          (c) =>
            c.type === "stack" ||
            c.type === "product-table" ||
            isTallMedia(c)
        )
      ) {
        break;
      }

      const converted = nextKids.map((c) => ({
        ...c,
        span: toStackInnerSpan(c.span, sideSpan),
        breakBeside: false,
      }));
      const trial = [...sideItems, ...converted];
      if (packRowsOnly(trial).length > maxBeside) break;
      sideItems = trial;
      j += 1;
    }

    if (sideItems.length) {
      result.push(createRow([tall, createStack(sideItems, sideSpan)]));
    } else {
      result.push(createRow([tall]));
    }
    i = Math.max(j, i + 1);
  }
  return result;
}

/**
 * Append a component beside tall media as a stack item when possible.
 * Returns new rows array or null if not inserted.
 */
export function tryAppendBesideTall(rows, comp, preferRowIndex = -1) {
  const span = Math.min(12, Math.max(1, comp.span || 12));
  const candidates = [];
  if (preferRowIndex >= 0) candidates.push(preferRowIndex);
  if (rows.length) candidates.push(rows.length - 1);
  for (let r = 0; r < rows.length; r++) {
    if (!candidates.includes(r)) candidates.push(r);
  }

  for (const rowIndex of candidates) {
    if (rowIndex < 0 || rowIndex >= rows.length) continue;
    const kids = [...(rows[rowIndex].children || [])];
    const tall = kids.find(
      (c) => c.type !== "stack" && isTallMedia(c) && (c.span || 12) < 12
    );
    if (!tall) continue;
    const sideSpan = 12 - Math.min(12, Math.max(1, tall.span || 12));
    if (isTallMedia(comp) || comp.type === "product-table") continue;

    const stackIdx = kids.findIndex((c) => c.type === "stack");
    const nextKids = kids.filter((c) => c.id !== tall.id && c.type !== "stack");
    let stackChildren = [];
    if (stackIdx >= 0) {
      stackChildren = [...(kids[stackIdx].children || [])];
    } else {
      stackChildren = nextKids.map((c) => ({
        ...c,
        span: toStackInnerSpan(c.span, sideSpan),
      }));
    }
    const incoming = {
      ...comp,
      span: toStackInnerSpan(span, sideSpan),
    };
    const trial = [...stackChildren, incoming];
    if (packRowsOnly(trial).length > getBesideRows(tall)) continue;
    const stack = createStack(trial, sideSpan);
    const nextRows = rows.map((row, i) =>
      i === rowIndex ? { ...row, children: [tall, stack] } : row
    );
    return nextRows;
  }
  return null;
}

/**
 * Remove a leaf from rows (top-level or inside a stack). Returns { rows, removed }.
 */
export function removeLeafFromRows(rows, leafId) {
  let removed = null;
  const nextRows = [];
  for (const row of rows || []) {
    const kids = row.children || [];
    let changed = false;
    const nextKids = [];
    for (const cell of kids) {
      if (cell.id === leafId) {
        removed = { ...cell };
        changed = true;
        continue;
      }
      if (cell.type === "stack") {
        const stackChildren = cell.children || [];
        if (stackChildren.some((c) => c.id === leafId)) {
          removed = { ...stackChildren.find((c) => c.id === leafId) };
          const left = stackChildren.filter((c) => c.id !== leafId);
          changed = true;
          if (left.length) {
            nextKids.push({ ...cell, children: left });
          }
          continue;
        }
      }
      nextKids.push(cell);
    }
    if (!changed) {
      nextRows.push(row);
    } else if (nextKids.length) {
      nextRows.push({ ...row, children: nextKids });
    }
  }
  return { rows: nextRows, removed };
}

/** Insert a leaf into a stack at index (or append). */
export function insertLeafIntoStack(rows, stackId, index, leaf) {
  return (rows || []).map((row) => ({
    ...row,
    children: (row.children || []).map((cell) => {
      if (cell.id !== stackId || cell.type !== "stack") return cell;
      const kids = [...(cell.children || [])];
      const at = Math.max(
        0,
        Math.min(kids.length, index == null ? kids.length : index)
      );
      kids.splice(at, 0, {
        ...leaf,
        breakBeside: false,
        span: toStackInnerSpan(leaf.span, cell.span || 10),
      });
      return { ...cell, children: kids };
    }),
  }));
}

/** Create or extend a side stack beside tall media in a row. */
export function insertLeafBesideTall(rows, rowIndex, leaf, atIndex = null) {
  return (rows || []).map((row, ri) => {
    if (ri !== rowIndex) return row;
    const kids = [...(row.children || [])];
    const tall = kids.find(
      (c) => c.type !== "stack" && isTallMedia(c) && (c.span || 12) < 12
    );
    if (!tall) return row;
    const sideSpan = 12 - Math.min(12, Math.max(1, tall.span || 12));
    const stackIdx = kids.findIndex((c) => c.type === "stack");
    const incoming = {
      ...leaf,
      breakBeside: false,
      span: toStackInnerSpan(leaf.span || 12, sideSpan),
    };
    if (stackIdx >= 0) {
      const stack = kids[stackIdx];
      const stackChildren = [...(stack.children || [])];
      const at =
        atIndex == null
          ? stackChildren.length
          : Math.max(0, Math.min(stackChildren.length, atIndex));
      stackChildren.splice(at, 0, incoming);
      if (packRowsOnly(stackChildren).length > getBesideRows(tall)) {
        return row;
      }
      kids[stackIdx] = { ...stack, children: stackChildren };
      return { ...row, children: kids };
    }
    return { ...row, children: [tall, createStack([incoming], sideSpan)] };
  });
}

/** Insert leaf as its own page row at rowIndex (detached from beside group). */
export function insertLeafAsRow(rows, rowIndex, leaf) {
  const list = [...(rows || [])];
  const at = Math.max(0, Math.min(list.length, rowIndex));
  list.splice(
    at,
    0,
    createRow([
      {
        ...leaf,
        breakBeside: true,
        span: Math.min(12, Math.max(1, leaf.span || 12)),
      },
    ])
  );
  return list;
}

export function createEmptyTemplate({
  name = "销售单模板",
  paperSize = "A4",
  lang = "zh",
} = {}) {
  return {
    id: uid("tpl"),
    name,
    paperSize,
    /** Template content language: zh (LTR) | ug (RTL + Alp Ekran). */
    lang: lang === "ug" ? "ug" : "zh",
    desktopEnabled: true,
    mobileEnabled: true,
    isDesktopDefault: false,
    isMobileDefault: false,
    copies: 1,
    updatedAt: new Date().toISOString(),
    layout: { rows: [] },
  };
}
